import { ClaimRecord, CreateClaimRecordPayload, CreateClaimRecordResult } from '@typesApp/ClaimRecord'
import { Customer, CustomerContext } from '@typesApp/Customer'
import { CartBundle, ProductBundle } from '@typesApp/Product'
import { formatCommerceCode } from '@utils/formatCommerceCode'

type ControllerService = {
  fetchCartBundle: () => Promise<CartBundle>
  putClaimRecord: (payload: CreateClaimRecordPayload) => Promise<ClaimRecord>
}

type EnvironmentOptions = {
  ecommerceCode: string | null
  ecommerceTransactionHost: string
  lang: string
  country: string
  channel: string | undefined
}

type CreateOptions = {
  insurerId?: string
  customer?: Customer
  cartBundle?: CartBundle
}

type CreateClaimRecordParams = {
  customer: Customer
  options?: CreateOptions
}

type CreateClaimRecordControllerReturn = {
  createClaimRecord: (params: CreateClaimRecordParams) => Promise<CreateClaimRecordResult> | never
}

export const createClaimRecordController = (
  service: ControllerService,
  environment: EnvironmentOptions,
  customerContext: CustomerContext | undefined
): CreateClaimRecordControllerReturn => {
  const { ecommerceCode, ecommerceTransactionHost, lang, country, channel } = environment

  const createClaimRecord = async (params: CreateClaimRecordParams): Promise<CreateClaimRecordResult> | never => {
    const { customer, options } = params
    let cartBundle = options?.cartBundle

    // fetch cart items unless already provided in options
    if (!cartBundle) {
      try {
        cartBundle = await service.fetchCartBundle()
      } catch {
        // create claim record w/o cart products on cart items error
        return await createClaimRecordDefault(params)
      }
    }

    validateProductBundlesOrThrow(cartBundle?.products)

    const payload = getPayloadFromCartItems(cartBundle, customer, options)
    const result = await service.putClaimRecord(payload)
    return result
  }

  const createClaimRecordDefault = async (params: CreateClaimRecordParams): Promise<CreateClaimRecordResult> => {
    const { customer, options } = params
    const payload = getDefaultPayload(customer, options)
    const result = await service.putClaimRecord(payload)
    return result
  }

  const getDefaultPayload = (customer: Customer, options?: CreateOptions): CreateClaimRecordPayload => {
    let payload: CreateClaimRecordPayload = {
      customerNumber: customer?.id,
      customerEmail: customer?.email,
      customerPhone: customer?.phoneNumber,
      customerName: {
        firstName: customer?.firstName,
        lastName: customer?.lastName,
      },
      eligibleProducts: [],
      locale: `${lang}_${country?.toUpperCase()}`,
      ecommerce: formatCommerceCode({
        ecommerceCode,
        ecommerceTransactionHost,
      }),
      channel,
    }

    if (!channel) {
      delete payload.channel
    }

    if (customerContext?.customerType === 'GUEST') {
      delete payload.customerName
      delete payload.customerEmail
      delete payload.customerPhone
    }

    // do not send empty name
    const emptyName = !customer?.firstName && !customer?.lastName
    if (emptyName) {
      delete payload.customerName
    }

    if (options?.insurerId) {
      payload = {
        ...payload,
        claimData: {
          primaryCoverage: {
            insurer: {
              id: options?.insurerId ?? '',
            },
          },
        },
      }
    }

    return payload
  }

  const getPayloadFromCartItems = (
    cartBundle: CartBundle,
    customer: Customer,
    options?: CreateOptions
  ): CreateClaimRecordPayload => {
    const payload: CreateClaimRecordPayload = {
      ...getDefaultPayload(customer, options),

      // Note: cartBundle.cartId may be undefined for
      // empty cart but commerce F/E provided a cartId
      cartId: cartBundle.cartId || customerContext?.cartId,
      eligibleProducts: cartBundle.products ?? [],
      orderSubtotal: cartBundle.orderSubtotal,
      shippingCosts: cartBundle.shippingCosts,
    }

    // empty string cartId not accepted on b/e
    if (!payload.cartId) {
      delete payload.cartId
    }

    return payload
  }

  const validateProductBundlesOrThrow = (products: ProductBundle[] | undefined) => {
    if (!products?.length) {
      return
    }

    products.forEach(p => {
      if (!p.id) {
        throw new Error('Product id missing')
      }

      if (!p.cartProducts?.length) {
        throw new Error('Each product bundle must have at least one cart product')
      }

      const missingClaimProductType = p.cartProducts.some(cp => !cp.claimProductTypes?.length)
      if (missingClaimProductType) {
        throw new Error('Each cart product must have at least one claim product type')
      }
    })
  }

  return {
    createClaimRecord,
  }
}
